/**
 * main.js — Spellroads entry point.
 * Waystone Interactive
 *
 * Boot order:
 *   1. DebugLog (must be first — captures all subsequent errors)
 *   2. Game version constant
 *   3. Renderer
 *   4. InputManager
 *   5. SceneManager
 *   6. DebugConsole
 *   7. UI singletons (Inventory, Skills, Town, Quest, Camping, Coop)
 *   8. Transition to boot scene, then main menu
 *
 * IMPORTANT: All static imports must be at the top level. All async operations
 * are wrapped in an async IIFE so this module evaluates synchronously.
 * This prevents a circular-dependency deadlock: lazy-loaded scene chunks
 * (e.g. BootScene) import shared code (DebugLog) from this bundle. If this
 * module has top-level await, the ES module system considers it "still
 * evaluating" when the scene chunk tries to import from it — causing a
 * permanent hang. The IIFE pattern completes module evaluation immediately,
 * making exports available to any module that imports from this bundle.
 */

// ─── 0. PixiJS browser extensions ─────────────────────────────────────────────
import 'pixi.js/browser';

// ─── Static imports ───────────────────────────────────────────────────────────
import DebugLog from './debug/DebugLog.js';
import renderer from './renderer/Renderer.js';
import InputManager from './input/InputManager.js';
import SceneManager from './core/SceneManager.js';
import DebugConsole from './debug/DebugConsole.js';
import InventoryManager from './ui/InventoryManager.js';
import SkillManager from './ui/SkillManager.js';
import TownPanel from './ui/TownPanel.js';
import QuestLogPanel from './ui/QuestLogPanel.js';
import CampingPanel from './ui/CampingPanel.js';
import ManagePlayersPanel from './ui/ManagePlayersPanel.js';
import StartingScenarioSystem from './systems/StartingScenarioSystem.js';
import AudioManager from './audio/AudioManager.js';
import SaveSystem from './save/SaveSystem.js';
import AchievementSystem from './systems/AchievementSystem.js';
import AchievementPanel from './ui/AchievementPanel.js';
import CompendiumPanel from './ui/CompendiumPanel.js';
import OnlineCoopSystem from './systems/OnlineCoopSystem.js';
import PrestigeSystem from './systems/PrestigeSystem.js';
import PrestigePanel from './ui/PrestigePanel.js';
import WorldEventSystem from './systems/WorldEventSystem.js';
import WorldEventPanel from './ui/WorldEventPanel.js';
import TownMenuPanel from './ui/TownMenuPanel.js';
import EventBus from './core/EventBus.js';
import LootSystem from './systems/LootSystem.js';
import EventSystem from './systems/EventSystem.js';
import FactionSystem from './systems/FactionSystem.js';
import FactionPanel from './ui/FactionPanel.js';
import CompanionSystem from './systems/CompanionSystem.js';

// ─── Boot sequence (async IIFE — module evaluates synchronously) ──────────────
(async () => {
  // 1. DebugLog — must be initialized before everything else
  DebugLog.init();
  window.__debugLog = DebugLog;

  // 2. Game version
  window.__gameVersion = '0.1.0';

  // 2a. Load art manifest (non-blocking — game works with placeholder colors if absent)
  fetch('./art/manifest.json')
    .then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
    .then(manifest => {
      window.__artManifest = manifest;
      DebugLog.info(`[boot] Art manifest loaded — v${manifest.version}`);
    })
    .catch(err => {
      window.__artManifest = {};
      DebugLog.warn(`[boot] Art manifest unavailable: ${err.message}`);
    });

  // Global settings — fog of war is off by default (beta feature)
  window.__settings = { fogOfWar: false };
  DebugLog.info(`[boot] Spellroads v${window.__gameVersion} starting`);

  // 3. Renderer
  DebugLog.info('[boot] Initializing renderer');
  await renderer.init();

  // 4. InputManager
  InputManager.init();
  window.__inputManager = InputManager;

  // 5. SceneManager
  SceneManager.init(renderer);

  // 6. DebugConsole
  DebugConsole.init();

  // 7. InventoryManager
  InventoryManager.init();
  window.__inventoryManager = InventoryManager;

  // 7b. SkillManager
  SkillManager.init();
  window.__skillManager = SkillManager;

  // Global panel hotkeys — `I` for inventory, `K` for skill trees
  window.addEventListener('keydown', (e) => {
    const tag = document.activeElement?.tagName?.toLowerCase();
    if (tag === 'input' || tag === 'textarea') return;
    if (DebugConsole.isOpen()) return;

    if (e.code === 'KeyI' && !e.ctrlKey && !e.altKey && !e.metaKey) {
      e.preventDefault();
      import('./core/EventBus.js').then(m => m.default.emit('input:inventory', { charId: null }));
    }

    if (e.code === 'KeyK' && !e.ctrlKey && !e.altKey && !e.metaKey) {
      e.preventDefault();
      import('./core/EventBus.js').then(m => m.default.emit('input:skills', { charId: null }));
    }

    if (e.code === 'KeyJ' && !e.ctrlKey && !e.altKey && !e.metaKey) {
      e.preventDefault();
      window.__questLogPanel?.toggle();
    }

    if (e.code === 'Tab' && !e.ctrlKey && !e.altKey && !e.metaKey && !e.shiftKey) {
      const active = document.activeElement;
      const inPanel = active?.closest?.('.spellroads-panel');
      if (!inPanel) {
        e.preventDefault();
        window.__managePlayersPanel?.toggle();
      }
    }

    if (e.code === 'KeyP' && !e.ctrlKey && !e.altKey && !e.metaKey) {
      e.preventDefault();
      window.__prestigePanel?.open();
    }
  });

  // 7c. TownPanel
  TownPanel.init();
  window.__townPanel = TownPanel;

  // 7d. QuestLogPanel
  QuestLogPanel.init();
  window.__questLogPanel = QuestLogPanel;

  // 7e. CampingPanel
  CampingPanel.init();
  window.__campingPanel = CampingPanel;

  // 7f. ManagePlayersPanel
  ManagePlayersPanel.init();
  window.__managePlayersPanel = ManagePlayersPanel;

  // 7g. StartingScenarioSystem — expose scenarios for debug API and Playwright tests
  window.__startingScenarios = StartingScenarioSystem.getAll();

  // 7h. AudioManager — initialized in BootScene; expose for debug API and scene use
  window.__audioManager = AudioManager;

  // 7i. SaveSystem
  SaveSystem.init();
  window.__saveSystem = SaveSystem;

  // 7j. AchievementSystem
  AchievementSystem.init();
  window.__achievementSystem = AchievementSystem;

  // 7j2. AchievementPanel — shown via main menu button or debug API
  const achievementPanel = new AchievementPanel();
  window.__achievementPanel = achievementPanel;

  // Expose debug API methods for AchievementSystem
  window.__debug = window.__debug ?? {};
  window.__debug.unlockAchievement = (id) => {
    const ok = AchievementSystem.unlock(id);
    return ok ? `Achievement unlocked: ${id}` : `Achievement already unlocked or unknown: ${id}`;
  };
  window.__debug.listAchievements = () => AchievementSystem.getProgressList();
  window.__debug.openAchievements = () => achievementPanel.show();

  // 7k. CompendiumPanel — open with C key (or debug API)
  const compendiumPanel = new CompendiumPanel();
  window.__compendiumPanel = compendiumPanel;

  // 7l. OnlineCoopSystem — expose for Playwright tests and debug API
  window.__onlineCoopSystem = OnlineCoopSystem;

  // 7m. PrestigeSystem — New Game+ prestige bonuses and world reset
  PrestigeSystem.init();
  window.__prestigeSystem = PrestigeSystem;

  // 7n. PrestigePanel — opened with P key or debug 'prestige' command
  const prestigePanel = new PrestigePanel();
  window.__prestigePanel = prestigePanel;

  // Start prestige eligibility check interval (once per minute)
  setInterval(() => PrestigeSystem.tick(), 60_000);

  // 7o. WorldEventSystem — dynamic world events during exploration
  await WorldEventSystem.init();
  window.__worldEventSystem = WorldEventSystem;

  // 7p. WorldEventPanel — modal panel for presenting world events
  const worldEventPanel = new WorldEventPanel();
  window.__worldEventPanel = worldEventPanel;

  // 7q. TownMenuPanel — rich town interaction panel
  window.__townMenuPanel = TownMenuPanel;

  // Expose global opener for WorldMapScene and other scenes to call
  window.__openTownMenu = (townData) => TownMenuPanel.open(townData);

  // 7r. LootSystem — loot table rolling and enemy loot mapping
  LootSystem.init();
  window.__lootSystem = LootSystem;

  // 7s. EventSystem — random travel events
  EventSystem.init();
  window.__eventSystem = EventSystem;
  // Re-register LootSystem debug API (module-level registration is wiped by DebugConsole._setupWindowDebug)
  window.__debug.rollLoot = (tableId) => {
    const results = LootSystem.rollTable(tableId);
    // eslint-disable-next-line no-console
    console.log(`[LootSystem] rollLoot('${tableId}'):`, JSON.stringify(results, null, 2));
    return results;
  };
  window.__debug.triggerRandomEvent = (eventId) => {
    const result = EventSystem.triggerEvent(eventId);
    return result.ok ? `Event triggered: ${result.event?.title ?? eventId}` : `Failed: ${result.reason}`;
  };

  // 7t. FactionSystem — faction reputation tracking
  FactionSystem.init();
  window.__factionSystem = FactionSystem;
  window.__debug.getFactionReputation = (id) => FactionSystem.getReputation(id);
  window.__debug.setFactionReputation = (id, val) => {
    FactionSystem._reputations.set(id, Math.max(-100, Math.min(100, val)));
    return `${id} reputation set to ${val}`;
  };
  // listFactions and openFactions registered by FactionSystem.init() itself

  // 7t2. FactionPanel — view reputation with all factions (singleton)
  window.__factionPanel = FactionPanel;

  // 7u. CompanionSystem — hireable NPC companions
  CompanionSystem.init();
  window.__companionSystem = CompanionSystem;
  window.__debug.listCompanions = () => CompanionSystem.getAllDefinitions().map(c => ({
    id: c.id, name: c.name, hired: CompanionSystem.isHired(c.id), cost: c.hireCost,
  }));
  window.__debug.hireCompanion = (id) => {
    const r = CompanionSystem.hire(id);
    return r.ok ? `Hired ${id}` : `Failed: ${r.reason}`;
  };
  window.__debug.dismissCompanion = (id) => {
    const r = CompanionSystem.dismiss(id);
    return r.ok ? `Dismissed ${id}` : `Failed: ${r.reason}`;
  };

  // EventBus bridge — LocalMapScene (and others) will emit 'town:enter'
  EventBus.on('town:enter', (townData) => TownMenuPanel.open(townData));

  DebugLog.info('[boot] TownMenuPanel registered');

  // Additional hotkeys: C for compendium, A for achievements
  window.addEventListener('keydown', (e) => {
    const tag = document.activeElement?.tagName?.toLowerCase();
    if (tag === 'input' || tag === 'textarea') return;
    if (DebugConsole.isOpen()) return;
    if (e.code === 'KeyC' && !e.ctrlKey && !e.altKey && !e.metaKey) {
      e.preventDefault();
      window.__compendiumPanel?.toggle();
    }
    if (e.code === 'KeyA' && !e.ctrlKey && !e.altKey && !e.metaKey) {
      e.preventDefault();
      window.__achievementPanel?.toggle();
    }
  });

  // 8. Boot scene
  DebugLog.info('[boot] Transitioning to boot scene');
  await SceneManager.transition('boot');

  // 9. Main menu
  DebugLog.info('[boot] Transitioning to main menu');
  await SceneManager.transition('mainMenu');

  DebugLog.info('[boot] Startup complete');
})();
